import config from "../config";
import {getData, getProperty} from "./http-request-utils";
import * as _ from 'lodash';
import {httpPost} from "./http-functions";
import {Machine, SearchOrgMachinesQuery, SearchOrgUnitMachineQuery, UpdateMachineTags} from "../models/machine.models";
import {ContactElement, MachineContact, SearchContacts, AddContactsResponse} from "../models/contacts.models";
import {KeyValue, PaginatedSearchResult} from "../models/shared";

const buildUrl = (apiRoot: string) => (suffix: string) => {
    return `${apiRoot}/${suffix}`;
};

const buildUrlFromConfig = buildUrl(config.api);

export const searchOrgUnitMachines = (httpFn: any) => {

    return async ({
                      organizationUnitId,
                      size,
                      page,
                      filter
                  }: SearchOrgUnitMachineQuery): Promise<PaginatedSearchResult<Machine>> => {
        const url = buildUrlFromConfig('Machines/search');
        const result = await httpFn(url, {from: page * size, size, filter, organizationUnitId});
        const payload: {items: Machine[], total: number} = getProperty('data')(result);

        return ({
            page,
            size,
            total: payload.total,
            items: payload.items
        });
    };
};

export const searchOrganizationMachines = (httpFn: any) => {

    return async ({
                      organizationId,
                      size,
                      page,
                      filter
                  }: SearchOrgMachinesQuery): Promise<PaginatedSearchResult<Machine>> => {
        const url = buildUrlFromConfig('Machines/search-all');
        const result = await httpFn(url, {from: page * size, size, filter, organizationId});
        const payload: {items: Machine[], total: number} = getProperty('data')(result);

        return ({
            page,
            size,
            total: payload.total,
            items: payload.items
        });
    };
};

export const getMachineTags = (httpFn: any) => async ({
                                                          machineId,
                                                          organizationUnitId
                                                      }: { machineId: string, organizationUnitId: number }): Promise<KeyValue<KeyValue<string>[]>[]> => {
    const url = buildUrlFromConfig(`tags?userId=${machineId}&organizationUnitId=${organizationUnitId}`);

    const result = await httpFn(url)
    const tags = getProperty('data')(result);

    return _
        .keys(tags)
        .map((k) => ({
            [k]: _.flatten(tags[k]).map((v: any) => ({
                key: v.Key,
                value: v.Value
            }))
        }))
        .reduce((acc: any, cur: any) => ({...acc, ...cur}), {});
};

export const updateMachineTags = (httpFn: any) => ({
                                                       organizationUnitId,
                                                       machineId,
                                                       tagId,
                                                       tags
                                                   }: UpdateMachineTags) => {
    const url = buildUrlFromConfig(`tags`);
    return httpFn(url, {tags: {[tagId]: tags}, userId: machineId, organizationUnitId: organizationUnitId});
};

export const searchContacts = (httpFn: any) => {
    return async ({
                      userId,
                      size,
                      page,
                      filter
                  }: SearchContacts): Promise<PaginatedSearchResult<MachineContact>> => {
        const url = buildUrlFromConfig('Relation/SearchMachineContacts');
        const result = await httpFn(url, {Body: {userId, page, size, filter}});
        return ({
            page,
            size,
            total: getProperty('TotalRows')(result),
            items: getProperty('Items')(result)
        });
    };
};

export const addContacts = async (userId: string, contacts: ContactElement[]): Promise<AddContactsResponse> => {
    const url = buildUrlFromConfig('Relation/AddMachineContacts');

    const resp = getData(await httpPost(url, {Body: {UserId: userId, ContactElements: contacts}}));
    return resp.Body;
};

export const removeContacts = async (userId: string, contactsToRemove: string[]): Promise<boolean> => {
    const url = buildUrlFromConfig('Relation/RemoveContacts');
    const resp = getData(await httpPost(url, {Body: {UserId: userId, contactIds: contactsToRemove}}));
    return resp.Body;
};

export const resetMachine = async (userId: string) => {
    const url = buildUrlFromConfig(`machines/${userId}/reset`);

    return httpPost(url, {Body: {UserId: userId}});
};

export const renameMachine = ({
                                  userId,
                                  friendlyName
                              }: { userId: string, friendlyName: string }) => {
    const url = buildUrlFromConfig(`machines/${userId}/rename`);
    return httpPost(url, {friendlyName});
}

export const moveMachines = async (request: {
    userId: string;
    sourceOrgUnitId: number;
    targetOrgUnitId: number;
}): Promise<any> => {
    const url = buildUrlFromConfig('machines/MoveMachine');
    await httpPost(url, request);
}
